import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbGrave2 } from "react-icons/tb";
import { useNavigate, useParams } from "react-router-dom";
import usePetViewOwnerId from "../hooks/usePetViewOwnerId";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { DECEASED_PET } from "../../../enums/ApiPaths";
import { DELETE_PET } from "../../../enums/PermissionTitles";
import { PETS, PETS_GENERAL } from "../../../enums/QueryKeys";
import * as urlPaths from "../../../enums/UrlPaths";
import useConfirm from "../../../hooks/useConfirm";
import usePut from "../../../hooks/usePut";
import useUser from "../../../hooks/useUser";
import { setLastPetIndex } from "../../../utils/Functions";

export default function DeceasedPet({ pet }) {
  const { id } = useParams();
  const ownerId = usePetViewOwnerId();
  const { roles, profile } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { control, setValue, getValues } = useForm();
  const { t } = useTranslation();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const keys = PETS_GENERAL + id;
  const url = DECEASED_PET + id;
  const put = usePut(url, keys);

  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("AreYouSure"),
    t("Accept"),
    t("Cancel"),
    "warning",
  );

  function getDeceasedMessage() {
    return (
      <Stack direction={"column"} alignItems={"center"} spacing={2}>
        <Typography>{t("DeceasedPetMessage")}</Typography>
        <Stack width={"100%"} spacing={2}>
          <Controller
            control={control}
            name="message"
            render={({ field }) => (
              <TextField
                {...field}
                label={`${t("Message")} (${t("Optional")})`}
                fullWidth
                onChange={field.onChange}
                inputProps={{
                  "data-testid": "pet.management.deceased-pet.message-input",
                }}
              />
            )}
          />

          <Controller
            name="date"
            control={control}
            render={({ field: { onChange, value } }) => (
              <MobileDatePicker
                disableFuture
                format="DD/MM/YYYY"
                value={value ?? dayjs()}
                onChange={(date) => setValue("date", date)}
                slotProps={{
                  textField: {
                    inputProps: {
                      "data-testid": "pet.management.deceased-pet.date-input",
                    },
                  },
                  previousIconButton: {
                    "data-testid":
                      "pet.management.deceased-pet.date-input.prev-month",
                  },
                  nextIconButton: {
                    "data-testid":
                      "pet.management.deceased-pet.date-input.next-month",
                  },
                  day: (ownerState) => ({
                    "data-testid": `pet.management.deceased-pet.date-input.day-${ownerState.day.format("YYYY-MM-DD")}`,
                  }),
                }}
              />
            )}
          />
        </Stack>
      </Stack>
    );
  }

  async function handleDeceasedPet(e) {
    e.preventDefault();
    getMessage(getDeceasedMessage());
    const ans = await confirm();
    const dateVal = dayjs(getValues("date")).format("DD/MM/YYYY");
    const descVal = getValues("message");

    const newPet = {
      date: dateVal,
      description: descVal,
    };
    
    if (ans) {
      const res = await put.submitCallback(newPet);
      if (res) {
        setLastPetIndex(0);
        queryClient.invalidateQueries([PETS + "user" + ownerId]);
        if (Number(profile.id) == Number(ownerId)) {
          navigate(urlPaths.PETS);
        } else if (roles.adminOrEmployee || roles.master) {
          navigate(urlPaths.SINGLE_CLINIC_USER + ownerId);
        } else {
          navigate(urlPaths.PETS);
        }
      }
    }
  }

  if (put.loading) {
    return <CircularProgress />;
  }

  if (isPermissionAllowed(DELETE_PET)) {
    return (
      <form onSubmit={(e) => handleDeceasedPet(e)}>
        <Button
          type="submit"
          color="error"
          variant="contained"
          startIcon={<TbGrave2 />}
          data-testid="pet.management.deceased-pet.button"
        >
          {t("DeceasedPet")}
        </Button>
        <ConfirmationDialog />
      </form>
    );
  }
}
